// Kundenakte — alles zu einem Kunden auf einer Seite.
//
// Oben: wer er ist, wie man ihn erreicht, und mit einem Klick eine neue
// Rechnung, ein Angebot oder einen Lieferschein für ihn.
// Darunter: Umsatz, Offenes, was er am meisten kauft, wie er zahlt.
// Dann der Umsatzverlauf, die gekauften Produkte und alle Dokumente —
// Rechnungen, Lieferscheine, Angebote, Aufträge, Gutschriften, Retouren —
// mit Ansehen/Drucken und Sprung in den Vorgang.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { belegOeffnen } from '../../belege/lib/drucken';
import { useDaten, euro, datum, menge as mengeText } from '../../../gemeinsam/lib/daten';
import { einheitAnzeige } from '../../artikel/lib/einheiten';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import type { KundenAkteDaten, KundenDokument } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import KundenFormular, { ausKunde } from '../komponenten/KundenFormular';

type T = (s: Schluessel) => string;
type Art = KundenDokument['art'];
type Filter = 'alle' | 'offen' | Art;

const ARTEN: Art[] = ['rechnung', 'lieferschein', 'angebot', 'auftrag', 'gutschrift', 'retoure'];
const ART_FARBE: Record<Art, string> = {
  rechnung: 'bg-marke-100 text-marke-800 dark:bg-marke-950 dark:text-marke-200',
  lieferschein: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  angebot: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  auftrag: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200',
  gutschrift: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
  retoure: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
};
const STATUS_FARBE: Record<string, 'neutral' | 'gruen' | 'rot' | 'gelb' | 'blau'> = {
  entwurf: 'neutral', gestellt: 'gelb', bezahlt: 'gruen', storniert: 'rot',
  gesendet: 'blau', angenommen: 'gruen', abgelehnt: 'rot', abgelaufen: 'neutral',
  offen: 'gelb', inArbeit: 'blau', erledigt: 'gruen', geliefert: 'gruen',
  eingelagert: 'gruen', erfasst: 'neutral',
};

const fuellen = (text: string, werte: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (_, k: string) => String(werte[k] ?? ''));

/** Wohin „Öffnen" führt: in den Vorgang auf seiner eigenen Seite. */
function ziel(d: { art: string; id: string }) {
  if (d.art === 'rechnung') return `/rechnungen?id=${d.id}`;
  if (d.art === 'retoure') return '/retouren';
  return `/belege?art=${d.art}&id=${d.id}`;
}

export default function KundenAkte() {
  const { id = '' } = useParams();
  const { t, sprache } = useSprache();
  const navigate = useNavigate();
  const { daten: akte, laedt, fehler, neuLaden } = useDaten<KundenAkteDaten>(`/customers/${id}/akte`, [id]);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'fehler'; text: string } | null>(null);
  const [filter, setFilter] = useState<Filter>('alle');
  const [alleProdukte, setAlleProdukte] = useState(false);

  // Lieferscheine, die schon an ihrer Rechnung hängen — unter „Alle" nicht
  // ein zweites Mal als eigene Zeile.
  const angehaengt = useMemo(() => new Set((akte?.dokumente ?? []).map(d => d.folge?.id).filter(Boolean) as string[]), [akte]);
  const anzahl = useMemo(() => {
    const z: Record<string, number> = { alle: (akte?.dokumente ?? []).filter(d => !angehaengt.has(d.id)).length, offen: 0 };
    for (const d of akte?.dokumente ?? []) {
      z[d.art] = (z[d.art] ?? 0) + 1;
      if ((d.offen ?? 0) > 0.005) z.offen += 1;
    }
    return z;
  }, [akte, angehaengt]);

  if (laedt && !akte) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (fehler || !akte) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <Link to="/kunden" className="text-sm text-marke-700 hover:underline dark:text-marke-300">← {t('kunden.alle')}</Link>
        <Hinweis art="fehler">{fehler || t('kunden.nichtGefunden')}</Hinweis>
      </div>
    );
  }

  const { kunde: k, kennzahlen: z } = akte;
  const jahr = new Date().getFullYear();
  const veraenderung = z.umsatzVorjahr.netto > 0
    ? Math.round(((z.umsatzJahr.netto - z.umsatzVorjahr.netto) / z.umsatzVorjahr.netto) * 100) : null;
  const dokumente = akte.dokumente.filter(d => filter === 'alle' ? !angehaengt.has(d.id)
    : filter === 'offen' ? (d.offen ?? 0) > 0.005 : d.art === filter);
  const produkte = alleProdukte ? akte.produkte : akte.produkte.slice(0, 8);
  const anschrift = [k.strasse, [k.plz, k.ort].filter(Boolean).join(' '), k.land && k.land !== 'Deutschland' ? k.land : ''].filter(Boolean).join(', ');

  async function loeschen() {
    if (!confirm(fuellen(t('kunden.loeschenFrage'), { name: k.name }))) return;
    try {
      await api(`/customers/${k.id}`, { methode: 'DELETE' });
      navigate('/kunden', { replace: true });
    } catch (e) {
      // Kunden mit Rechnungen sind geschützt — die Meldung erklärt warum.
      setMeldung({ art: 'fehler', text: e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.' });
    }
  }

  async function ansehen(d: KundenDokument) {
    if (!d.druck) return;
    const problem = await belegOeffnen(apiUrl(`/api${d.druck}`));
    if (problem) setMeldung({ art: 'fehler', text: problem });
  }

  const neuLink = (pfad: string, text: string, primaer = false) => (
    <Link to={pfad}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition ${primaer
            ? 'bg-marke-600 text-white hover:bg-marke-700'
            : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'}`}>
      + {text}
    </Link>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Link to="/kunden" className="text-sm text-marke-700 hover:underline dark:text-marke-300">← {t('kunden.alle')}</Link>

      {meldung && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}

      {bearbeiten ? (
        <KundenFormular start={ausKunde(k)} beiAbbruch={() => setBearbeiten(false)}
                        beiFertig={() => { setBearbeiten(false); setMeldung({ art: 'erfolg', text: t('kunden.gespeichert') }); void neuLaden(); }} />
      ) : (
        <Karte klasse="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {k.name}
                {k.kundennummer && <span className="ms-2 text-base font-normal text-slate-500 dark:text-slate-400">#{k.kundennummer}</span>}
              </h1>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                {k.telefon && <a href={`tel:${k.telefon.replace(/[^\d+]/g, '')}`} className="font-medium text-marke-700 hover:underline dark:text-marke-300">{k.telefon}</a>}
                {k.email && <a href={`mailto:${k.email}`} className="truncate font-medium text-marke-700 hover:underline dark:text-marke-300">{k.email}</a>}
                {anschrift && <span>{anschrift}</span>}
                {k.ustId && <span>{t('feld.ustId')}: {k.ustId}</span>}
                {!k.telefon && !k.email && !anschrift && <span>{t('kunden.keineKontakt')}</span>}
              </div>
              {k.notiz && (
                <p className="mt-3 max-w-2xl whitespace-pre-line rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">{k.notiz}</p>
              )}
            </div>
            <div className="flex gap-1">
              <Knopf variante="still" onClick={() => { setBearbeiten(true); setMeldung(null); }}>{t('aktion.bearbeiten')}</Knopf>
              {z.rechnungen === 0 && akte.dokumente.length === 0 && (
                <Knopf variante="still" className="!text-red-600" onClick={loeschen}>{t('aktion.loeschen')}</Knopf>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {neuLink(`/rechnungen?neu=1&kunde=${k.id}`, t('kunden.rechnungSchreiben'), true)}
            {neuLink(`/belege?neu=angebot&kunde=${k.id}`, t('kunden.art.angebot'))}
            {neuLink(`/belege?neu=auftrag&kunde=${k.id}`, t('kunden.art.auftrag'))}
            {neuLink(`/belege?neu=lieferschein&kunde=${k.id}`, t('kunden.art.lieferschein'))}
          </div>
        </Karte>
      )}

      {/* ── Kennzahlen ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kachel titel={fuellen(t('kunden.umsatzJahr'), { jahr })} wert={euro(z.umsatzJahr.netto)}
                zusatz={<>
                  {t('kunden.netto')}{z.umsatzVorjahr.netto !== 0 && <> · {fuellen(t('kunden.vorjahr'), { betrag: euro(z.umsatzVorjahr.netto) })}</>}
                  {veraenderung !== null && (
                    <span className={`ms-1 font-medium ${veraenderung >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {veraenderung >= 0 ? '▲' : '▼'} {Math.abs(veraenderung)} %
                    </span>
                  )}
                </>} />
        <Kachel titel={t('kunden.offen')} wert={euro(z.offen)} warnung={z.ueberfaelligAnzahl > 0}
                zusatz={z.ueberfaelligAnzahl > 0
                  ? <span className="font-medium text-red-600 dark:text-red-400">{fuellen(t('kunden.ueberfaellig'), { betrag: euro(z.ueberfaellig), n: z.ueberfaelligAnzahl })}</span>
                  : fuellen(t('kunden.offenAnzahl'), { n: z.offenAnzahl })}
                beiKlick={z.offenAnzahl ? () => setFilter('offen') : undefined} />
        <Kachel titel={t('kunden.meistgekauft')} wert={akte.meistgekauft?.bezeichnung ?? '—'} klein
                zusatz={akte.meistgekauft ? `${fuellen(t('kunden.malGekauft'), { n: akte.meistgekauft.kaeufe })} · ${euro(akte.meistgekauft.netto)}` : t('kunden.keineProdukte')} />
        <Kachel titel={t('kunden.zahlung')}
                wert={z.zahlungsdauerTage === null ? '—' : fuellen(t('kunden.zahltNach'), { n: z.zahlungsdauerTage })}
                zusatz={z.puenktlichQuote === null ? t('kunden.nochKeineZahlung') : fuellen(t('kunden.puenktlich'), { n: z.puenktlichQuote })} />
      </div>
      <p className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-slate-500 dark:text-slate-400">
        {z.ersterKauf && <span>{fuellen(t('kunden.seit'), { datum: datum(z.ersterKauf) })}</span>}
        {z.letzterKauf && <span>{fuellen(t('kunden.letzterKauf'), { datum: datum(z.letzterKauf) })}</span>}
        <span>{fuellen(t('kunden.gesamt'), { betrag: euro(z.umsatzGesamt.netto) })}</span>
        {z.rechnungen > 0 && <span>{fuellen(t('kunden.durchschnitt'), { betrag: euro(z.durchschnitt) })}</span>}
        {!!z.guthaben && <span className="font-medium text-emerald-700 dark:text-emerald-400">{fuellen(t('kunden.guthaben'), { betrag: euro(z.guthaben) })}</span>}
      </p>

      {/* ── Verlauf und Produkte ──────────────────────────────────── */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Karte>
          <KartenKopf titel={t('kunden.verlauf')} hinweis={t('kunden.netto')} />
          <Verlauf monate={akte.monate} t={t} sprache={sprache} />
        </Karte>

        <Karte>
          <KartenKopf titel={t('kunden.produkte')} />
          {!akte.produkte.length ? <LeerZustand titel={t('kunden.keineProdukte')} /> : (
            <>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="px-5 py-2 text-start font-medium">{t('feld.position')}</th>
                    <th className="px-2 py-2 text-end font-medium">{t('kunden.kaeufe')}</th>
                    <th className="px-2 py-2 text-end font-medium">{t('kunden.menge')}</th>
                    <th className="px-5 py-2 text-end font-medium">{t('kunden.umsatz')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {produkte.map((p, i) => (
                    <tr key={p.schluessel}>
                      <td className="px-5 py-2.5">
                        <span className="me-2 inline-block w-4 text-xs text-slate-400">{i + 1}</span>
                        <span className="text-slate-900 dark:text-slate-100">{p.bezeichnung}</span>
                        {p.zuletzt && <span className="block ps-6 text-xs text-slate-500 dark:text-slate-400">{fuellen(t('kunden.zuletzt'), { datum: datum(p.zuletzt) })}</span>}
                      </td>
                      <td className="px-2 py-2.5 text-end tabular-nums text-slate-700 dark:text-slate-300">{p.kaeufe}×</td>
                      <td className="px-2 py-2.5 text-end tabular-nums text-slate-700 dark:text-slate-300">{mengeText(p.menge, einheitAnzeige(p.einheit, t))}</td>
                      <td className="px-5 py-2.5 text-end font-medium tabular-nums text-slate-900 dark:text-slate-100">{euro(p.netto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {akte.produkte.length > 8 && (
                <button type="button" onClick={() => setAlleProdukte(x => !x)}
                        className="w-full border-t border-slate-200 py-2.5 text-sm font-medium text-marke-700 hover:bg-slate-50 dark:border-slate-800 dark:text-marke-300 dark:hover:bg-slate-800/40">
                  {alleProdukte ? t('kunden.weniger') : fuellen(t('kunden.mehr'), { n: akte.produkte.length })}
                </button>
              )}
            </>
          )}
        </Karte>
      </div>

      {/* ── Dokumente ─────────────────────────────────────────────── */}
      <Karte>
        <KartenKopf titel={t('kunden.dokumente')} hinweis={fuellen(t('kunden.dokumenteHinweis'), { n: anzahl.alle })} />
        <div className="flex flex-wrap gap-1.5 border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          {(['alle', 'offen', ...ARTEN] as Filter[]).filter(f => f === 'alle' || anzahl[f]).map(f => (
            <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === f
                      ? 'bg-marke-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}>
              {f === 'alle' ? t('kunden.alleDokumente') : f === 'offen' ? t('kunden.nurOffene') : t(`kunden.arten.${f}`)}
              <span className="ms-1 opacity-70">{anzahl[f]}</span>
            </button>
          ))}
        </div>
        {!dokumente.length ? <LeerZustand titel={t('kunden.keineDokumente')} /> : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {dokumente.map(d => (
              <DokumentZeile key={`${d.art}:${d.id}`} d={d} t={t}
                             beiAnsehen={() => ansehen(d)} beiOeffnen={() => navigate(ziel(d))}
                             beiBezug={b => navigate(ziel(b))}
                             beiFolgeAnsehen={f => ansehen({ ...d, druck: `/belege/${f.id}/druck` })} />
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}

function Kachel({ titel, wert, zusatz, warnung, klein, beiKlick }: {
  titel: string; wert: string; zusatz?: React.ReactNode; warnung?: boolean; klein?: boolean; beiKlick?: () => void;
}) {
  const Inhalt = (
    <>
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{titel}</p>
      <p className={`mt-1 font-bold tabular-nums ${klein ? 'line-clamp-2 text-base leading-snug' : 'truncate text-xl'} ${warnung ? 'text-red-700 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`} title={wert}>{wert}</p>
      {zusatz && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{zusatz}</p>}
    </>
  );
  const klasse = 'rounded-xl border border-slate-200 bg-white p-4 text-start dark:border-slate-800 dark:bg-slate-900';
  return beiKlick
    ? <button type="button" onClick={beiKlick} className={`${klasse} transition hover:border-marke-300 dark:hover:border-marke-700`}>{Inhalt}</button>
    : <div className={klasse}>{Inhalt}</div>;
}

/** Status in der Sprache des Belegs: ein Lieferschein im Entwurf ist „nicht geliefert", eine gestellte Gutschrift „ausgestellt". */
function statusVon(d: KundenDokument): { schluessel: Schluessel; farbe: 'neutral' | 'gruen' | 'rot' | 'gelb' | 'blau' } {
  if (d.ueberfaellig) return { schluessel: 'aufgaben.ueberfaellig', farbe: 'rot' };
  if (d.art === 'lieferschein' && d.status === 'entwurf') return { schluessel: 'kunden.status.nichtGeliefert', farbe: 'neutral' };
  if (d.art === 'gutschrift' && d.status === 'gestellt') return { schluessel: 'kunden.status.ausgestellt', farbe: 'blau' };
  if (d.art === 'rechnung' && d.status === 'gestellt' && d.brutto !== null && (d.offen ?? 0) > 0.005 && (d.offen ?? 0) < d.brutto - 0.005) {
    return { schluessel: 'kunden.status.teilbezahlt', farbe: 'gelb' };
  }
  return { schluessel: `kunden.status.${d.status}` as Schluessel, farbe: STATUS_FARBE[d.status] ?? 'neutral' };
}

function DokumentZeile({ d, t, beiAnsehen, beiOeffnen, beiBezug, beiFolgeAnsehen }: {
  d: KundenDokument; t: T; beiAnsehen: () => void; beiOeffnen: () => void; beiBezug: (b: { art: string; id: string }) => void;
  beiFolgeAnsehen: (f: { id: string }) => void;
}) {
  const offen = (d.offen ?? 0) > 0.005;
  const status = statusVon(d);
  const verweis = (b: NonNullable<KundenDokument['bezug']>) => (
    <button type="button" onClick={() => beiBezug(b)} className="text-marke-700 hover:underline dark:text-marke-300">
      {fuellen(t('kunden.zu'), { nummer: b.nummer || t(`kunden.art.${b.art as Art}`) })}
    </button>
  );
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 text-sm">
      <div className="min-w-0 flex-1 basis-[16rem]">
        <div className="flex items-baseline gap-2">
          <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-center text-[11px] font-medium sm:w-28 sm:px-2 sm:text-xs ${ART_FARBE[d.art]}`}>{t(`kunden.art.${d.art}`)}</span>
          <button type="button" onClick={beiOeffnen} className="min-w-0 truncate text-start font-medium text-slate-900 hover:text-marke-700 hover:underline dark:text-slate-100 dark:hover:text-marke-300">
            {d.nummer || (d.art === 'retoure' ? t('kunden.art.retoure') : t('kunden.entwurf'))}
          </button>
          {d.brutto !== null && (
            <span className="ms-auto shrink-0 font-medium tabular-nums text-slate-900 dark:text-slate-100">
              {euro(d.art === 'gutschrift' || d.art === 'retoure' ? -d.brutto : d.brutto)}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 sm:ps-[7.5rem] dark:text-slate-400">
          <span>{datum(d.datum)}</span>
          {d.bezug && verweis(d.bezug)}
          {d.folge && (
            <button type="button" onClick={() => beiFolgeAnsehen(d.folge!)} className="text-marke-700 hover:underline dark:text-marke-300">
              {t('kunden.art.lieferschein')} {d.folge.nummer}
            </button>
          )}
          {d.faelligAm && offen && <span className={d.ueberfaellig ? 'font-medium text-red-600 dark:text-red-400' : ''}>{fuellen(t('kunden.faellig'), { datum: datum(d.faelligAm) })}</span>}
          {d.gueltigBis && d.status === 'gesendet' && <span>{fuellen(t('kunden.gueltigBis'), { datum: datum(d.gueltigBis) })}</span>}
          {d.grund && <span>{d.grund}</span>}
          <span className="ms-auto flex items-center gap-2">
            {offen && <span className="tabular-nums text-amber-700 dark:text-amber-400">{fuellen(t('kunden.offenBetrag'), { betrag: euro(d.offen) })}</span>}
            <Abzeichen art={status.farbe}>{t(status.schluessel)}</Abzeichen>
          </span>
        </div>
      </div>
      <div className="ms-auto flex gap-1">
        {d.druck && <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={beiAnsehen}>{t('kunden.ansehen')}</Knopf>}
        <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={beiOeffnen}>{t('kunden.oeffnen')} →</Knopf>
      </div>
    </li>
  );
}

/** Zwölf Monate als Säulen. Eine Reihe, eine Farbe; Zahlen im Tooltip und in der Tabelle für Vorleser. */
function Verlauf({ monate, t, sprache }: { monate: KundenAkteDaten['monate']; t: T; sprache: string }) {
  const [zeige, setZeige] = useState<number | null>(null);
  const max = Math.max(0, ...monate.map(m => m.netto));
  const name = (m: string, art: 'short' | 'long' = 'short') =>
    new Date(`${m}-15T12:00:00`).toLocaleDateString(sprache, art === 'short' ? { month: 'short' } : { month: 'long', year: 'numeric' });
  if (max <= 0) return <LeerZustand titel={t('kunden.keinUmsatz')} />;
  return (
    <div className="px-5 pb-4 pt-3">
      <div className="relative">
        <p className="absolute -top-1 start-0 text-[11px] tabular-nums text-slate-400">{euro(max)}</p>
        <div className="flex h-36 items-end gap-[2px] border-b border-slate-300 pt-4 dark:border-slate-700" role="img" aria-label={t('kunden.diagrammLabel')}>
          {monate.map((m, i) => (
            <button key={m.monat} type="button"
                    onMouseEnter={() => setZeige(i)} onMouseLeave={() => setZeige(null)}
                    onFocus={() => setZeige(i)} onBlur={() => setZeige(null)}
                    aria-label={`${name(m.monat, 'long')}: ${euro(m.netto)}`}
                    className="group relative flex h-full flex-1 items-end justify-center">
              <span className={`block w-full max-w-[28px] rounded-t transition-colors ${zeige === i ? 'bg-marke-700 dark:bg-marke-400' : 'bg-marke-500 dark:bg-marke-500'}`}
                    style={{ height: `${Math.max(m.netto > 0 ? 2 : 0, (Math.max(0, m.netto) / max) * 100)}%` }} />
              {zeige === i && (
                <span className="pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg dark:bg-slate-100 dark:text-slate-900">
                  {name(m.monat, 'long')}<br /><strong className="tabular-nums">{euro(m.netto)}</strong>
                  {m.anzahl > 0 && <> · {fuellen(t('kunden.rechnungenAnzahl'), { n: m.anzahl })}</>}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="mt-1 flex gap-[2px]">
          {monate.map((m, i) => (
            <span key={m.monat} className={`flex-1 text-center text-[10px] text-slate-500 dark:text-slate-400 ${i % 2 ? 'invisible sm:visible' : ''}`}>{name(m.monat)}</span>
          ))}
        </div>
      </div>
      <table className="sr-only">
        <caption>{t('kunden.diagrammLabel')}</caption>
        <tbody>{monate.map(m => <tr key={m.monat}><th>{name(m.monat, 'long')}</th><td>{euro(m.netto)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
